import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import * as yup from "yup";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core/styles";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Alert from "@material-ui/lab/Alert";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { useAppContext } from "../../api/AppContext";
import {
  ensurePromptMultilingualObject,
  formatLanguageList,
  getCompletionState,
  getEditableLanguagesForPromptSection,
  getIncompleteLanguages,
  getMandatoryLanguagesForPromptSection,
  getPreferredLanguageFromUser,
  getValidLanguagesFromUser,
  isTagNameCompleteForSave,
  LANGUAGE_LABELS,
  requiresAllMandatoryLanguages,
} from "../shared/languageConfig";
import { LanguageChips, PinnedLanguageField } from "../shared/LanguageSelector";
import { usePreferredLanguagePersistence } from "../shared/usePreferredLanguagePersistence";

const useStyles = makeStyles({
  selectedMenuItem: {
    backgroundColor: "yellow !important",
  },
  fixedWidthSelect: {
    width: "100%",
  },
  chipsContainer: {
    display: "flex",
    flexWrap: "nowrap",
    overflow: "hidden",
    gap: 4,
  },
  chip: {
    maxWidth: 140,
  },
});

const Tags = ({
  basePath,
  configurations,
  formik,
  preferredLanguage,
  setPreferredLanguage,
  validLanguages,
}) => {
  const classes = useStyles();

  const handleInputChange = (name, value, lang) => {
    if (lang) {
      formik.setFieldValue(name, {
        ...formik.values[name],
        [lang]: value,
      });
    } else {
      formik.setFieldValue(name, value);
    }
  };

  const secondaryLanguages = validLanguages.filter(
    (language) => language !== preferredLanguage
  );
  const [selectedPreferredLanguage, setSelectedPreferredLanguage] =
    useState(preferredLanguage);
  const [activeSecondaryLanguage, setActiveSecondaryLanguage] = useState(
    secondaryLanguages[0] || null
  );
  const persistPreferredLanguage = usePreferredLanguagePersistence();

  const handleConfirmPreferredLanguage = async () => {
    setPreferredLanguage(selectedPreferredLanguage);
    await persistPreferredLanguage(selectedPreferredLanguage);
  };

  useEffect(() => {
    setSelectedPreferredLanguage(preferredLanguage);
  }, [preferredLanguage]);

  useEffect(() => {
    if (!secondaryLanguages.length) {
      setActiveSecondaryLanguage(null);
      return;
    }

    if (!secondaryLanguages.includes(activeSecondaryLanguage)) {
      setActiveSecondaryLanguage(secondaryLanguages[0]);
    }
  }, [activeSecondaryLanguage, secondaryLanguages]);

  const incompleteMandatoryLanguages = useMemo(() => {
    if (!requiresAllMandatoryLanguages(basePath)) {
      return [];
    }

    return getIncompleteLanguages(
      getMandatoryLanguagesForPromptSection(basePath),
      (language) => isTagNameCompleteForSave(formik.values.tagname, language)
    );
  }, [basePath, formik.values.tagname]);

  const missingMandatoryLanguagesMessage = useMemo(() => {
    if (formik.submitCount === 0 || !incompleteMandatoryLanguages.length) {
      return null;
    }

    return `Saving requires these languages to be fully filled: ${formatLanguageList(
      incompleteMandatoryLanguages
    )}. Switch language chips to complete the missing translations.`;
  }, [formik.submitCount, incompleteMandatoryLanguages]);

  const languageCompletionByCode = useMemo(() => {
    return validLanguages.reduce((acc, language) => {
      const completed = formik.values.tagname?.[language]?.trim() ? 1 : 0;

      acc[language] = getCompletionState({
        completedLanguage: completed,
        isActive: language === activeSecondaryLanguage,
        totalRequiredFields: 1,
      });
      return acc;
    }, {});
  }, [activeSecondaryLanguage, formik.values.tagname, validLanguages]);

  const renderLanguageColumn = (language) => {
    if (!language) {
      return null;
    }

    const languageLabel = LANGUAGE_LABELS[language] || language.toUpperCase();
    const hasAtLeastOneCompleteName = validLanguages.some((lang) =>
      isTagNameCompleteForSave(formik.values.tagname, lang)
    );
    const showAtLeastOneFieldHint =
      !requiresAllMandatoryLanguages(basePath) &&
      formik.submitCount > 0 &&
      !hasAtLeastOneCompleteName &&
      language === preferredLanguage &&
      !formik.values.tagname?.[language]?.trim();

    const showNameError =
      ((formik.touched.tagname?.[language] || formik.submitCount > 0) &&
        Boolean(formik.errors.tagname?.[language])) ||
      showAtLeastOneFieldHint;

    const nameHelperText = formik.errors.tagname?.[language]
      ? formik.touched.tagname?.[language] || formik.submitCount > 0
        ? formik.errors.tagname[language]
        : undefined
      : showAtLeastOneFieldHint
      ? "Name is mandatory"
      : undefined;

    return (
      <Grid item xs={12}>
        <TextField
          fullWidth
          InputLabelProps={{ shrink: true }}
          error={showNameError}
          helperText={nameHelperText}
          label={`Name (${languageLabel})`}
          margin="none"
          value={formik.values.tagname?.[language] || ""}
          variant="outlined"
          onBlur={() => formik.setFieldTouched(`tagname.${language}`, true)}
          onChange={(event) =>
            handleInputChange("tagname", event.target.value, language)
          }
        />
      </Grid>
    );
  };

  return (
    <Box
      alignContent="center"
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      height="100%"
      p={4}
      width="100%"
    >
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item md={secondaryLanguages.length ? 6 : 12} xs={12}>
            <Box mb={2} style={{ minHeight: 40 }}>
              <PinnedLanguageField
                requireSetConfirmation
                showInlineLabel
                inlineLabel="Select default language"
                validLanguages={validLanguages}
                value={selectedPreferredLanguage}
                onChange={setSelectedPreferredLanguage}
                onConfirm={handleConfirmPreferredLanguage}
              />
            </Box>
            <Grid container spacing={2}>
              {renderLanguageColumn(preferredLanguage)}
            </Grid>
          </Grid>
          {secondaryLanguages.length > 0 && (
            <Grid item md={6} xs={12}>
              <Box mb={2} style={{ minHeight: 40 }}>
                <LanguageChips
                  languageCompletionByCode={languageCompletionByCode}
                  languages={secondaryLanguages}
                  onLanguageClick={setActiveSecondaryLanguage}
                />
              </Box>
              <Grid container spacing={2}>
                {renderLanguageColumn(activeSecondaryLanguage)}
              </Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              select
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                multiple: true,
                onChange: (event) => {
                  const value = event.target.value;
                  formik.setFieldValue(
                    "cid",
                    Array.isArray(value) ? value : [value]
                  );
                },
                value: formik.values.cid || [],
                renderValue: (selected) => {
                  const items = Array.isArray(selected) ? selected : [];
                  const maxVisible = 2;
                  const visible = items.slice(0, maxVisible);
                  const overflow = items.length - visible.length;
                  return (
                    <div className={classes.chipsContainer}>
                      {visible.map((cid) => (
                        <Chip
                          key={cid}
                          className={classes.chip}
                          label={cid}
                          size="small"
                        />
                      ))}
                      {overflow > 0 && (
                        <Chip
                          className={classes.chip}
                          label={`+${overflow}`}
                          size="small"
                        />
                      )}
                    </div>
                  );
                },
              }}
              className={classes.fixedWidthSelect}
              error={formik.touched.cid && Boolean(formik.errors.cid)}
              helperText={formik.touched.cid && formik.errors.cid}
              label="CID"
              margin="none"
              variant="outlined"
            >
              {(configurations || [])
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((configuration) => (
                  <MenuItem
                    key={configuration.cid}
                    className={
                      (formik.values.cid || []).includes(configuration.cid)
                        ? classes.selectedMenuItem
                        : ""
                    }
                    value={configuration.cid}
                  >
                    {configuration.name}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>

          <Grid container item justifyContent="center" mt={2} xs={12}>
            <Typography align="center" color="textSecondary">
              Color
            </Typography>
            <input
              style={{ marginLeft: "0.5rem" }}
              type="color"
              value={formik.values.tagcolor}
              onChange={(event) =>
                handleInputChange("tagcolor", event.target.value)
              }
            />
          </Grid>
        </Grid>

        <Box alignItems="center" display="flex" flexDirection="column" mt={2}>
          {missingMandatoryLanguagesMessage && (
            <Box maxWidth={640} mb={2} width="100%">
              <Alert severity="warning">
                {missingMandatoryLanguagesMessage}
              </Alert>
            </Box>
          )}
          <Button
            color="primary"
            type="submit"
            variant="contained"
            onClick={formik.handleSubmit}
          >
            Submit
          </Button>
        </Box>
      </Grid>
    </Box>
  );
};

const TagsContainer = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    createNewsTag,
    updateNewsTag,
    configurations,
    fetchConfigurations,
    user,
  } = useAppContext();
  const [configurationEntity, setConfigurationEntity] = useState(null);
  const { tag } = location.state || {};
  const basePath = "news";
  const validLanguages = useMemo(() => getValidLanguagesFromUser(user), [user]);
  const editableLanguages = useMemo(
    () => getEditableLanguagesForPromptSection(basePath, validLanguages),
    [basePath, validLanguages]
  );
  const [preferredLanguage, setPreferredLanguage] = useState(() =>
    getPreferredLanguageFromUser(user, editableLanguages)
  );

  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  useEffect(() => {
    setPreferredLanguage(getPreferredLanguageFromUser(user, editableLanguages));
  }, [user, editableLanguages]);

  useEffect(() => {
    if (tag) {
      setConfigurationEntity({
        id: tag.id,
        tagcolor: tag.color || tag.tagcolor || "#000",
        tagname: ensurePromptMultilingualObject(
          tag.tagname || {
            en: tag.name_en,
            it: tag.name_it,
            fr: tag.name_fr,
            es: tag.name_es,
          }
        ),
        cid: Array.isArray(tag.cids) ? tag.cids : tag.cid ? [tag.cid] : [],
      });
    }
  }, [location.state, id, tag]);

  const validationSchema = useMemo(() => {
    if (requiresAllMandatoryLanguages(basePath)) {
      return yup.object({
        tagcolor: yup.string().required("Color is required"),
        cid: yup.array().of(yup.string().max(100)).min(1, "CID is required"),
        tagname: yup.object().shape(
          getMandatoryLanguagesForPromptSection(basePath).reduce(
            (shape, language) => {
              shape[language] = yup
                .string()
                .trim()
                .required("Name is mandatory");
              return shape;
            },
            {}
          )
        ),
      });
    }

    return yup
      .object({
        tagcolor: yup.string().required("Color is required"),
        cid: yup.array().of(yup.string().max(100)).min(1, "CID is required"),
        tagname: yup.object(),
      })
      .test(
        "at-least-one-language-tagname",
        "At least one language must have a name",
        (value) =>
          editableLanguages.some((language) =>
            isTagNameCompleteForSave(value?.tagname, language)
          )
      );
  }, [basePath, editableLanguages]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: configurationEntity || {
      isNew: true,
      tagcolor: "#000",
      cid: [],
      tagname: ensurePromptMultilingualObject(),
    },
    onSubmit: async (values) => {
      try {
        const payload = {
          tagcolor: values.tagcolor,
          tagname: ensurePromptMultilingualObject(values.tagname),
          cids: Array.isArray(values.cid) ? values.cid : [],
        };
        if (values.id) {
          await updateNewsTag({ id: values.id, ...payload });
        } else {
          await createNewsTag(payload);
        }
        enqueueSnackbar("Tag saved successfully!", { variant: "success" });
        history.push("/news/tags");
      } catch (error) {
        enqueueSnackbar("Error saving tag", { variant: "error" });
      }
    },
    validationSchema,
    validator: () => ({}),
  });

  if (!tag && !formik.values.isNew) {
    return (
      <Box
        alignItems="center"
        display="flex"
        justifyContent="center"
        width="100%"
      >
        <Typography variant="subtitle1">Tag is not found 😢</Typography>
      </Box>
    );
  }

  return (
    <Tags
      basePath={basePath}
      configurations={configurations}
      formik={formik}
      preferredLanguage={preferredLanguage}
      setPreferredLanguage={setPreferredLanguage}
      validLanguages={editableLanguages}
    />
  );
};

export default TagsContainer;
